import { FC } from "react";
import styled from "styled-components";
import Colors from "../global/Colors";
import Fonts from "../global/Fonts";

const LabelContainer = styled.div<{ $size: LabelRowSize }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 10px;
    padding: ${({ $size }) => getContainerPadding($size)};
`;

const LabelAndUnitsRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    margin: auto 0 auto 0;
`;

const Label = styled.h4<{ $size: LabelRowSize }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: ${({ $size }) => getLabelFontSize($size)};
    line-height: 1.2em;
    text-align: left;
    text-wrap: nowrap;
    color: ${({ $size }) => getLabelFontColor($size)};
    margin: auto;
`;

const ValueRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 3px;
    margin: auto 0 auto 0;
`;

const Value = styled.p<{ $size: LabelRowSize }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    font-family: ${Fonts.lexendBold.fontFamily};
    font-weight: ${Fonts.lexendBold.fontWeight};
    font-size: ${({ $size }) => getLabelFontSize($size)};
    line-height: 1.2em;
    text-align: right;
    text-wrap: nowrap;
    color: ${({ $size }) => getValueFontColor($size)};
    margin: auto 0 auto 0;
`;

const Units = styled.span<{ $size: LabelRowSize }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    font-family: ${Fonts.lexendLight.fontFamily};
    font-weight: ${Fonts.lexendLight.fontWeight};
    font-size: ${({ $size }) => getLabelFontSize($size)};
    line-height: 1.2em;
    text-align: right;
    text-wrap: nowrap;
    color: ${({ $size }) => getUnitsFontColor($size)};
    margin: auto 0 auto 0;
`;

type LabelRowSize = "default" | "mini";
type UnitsPosition = "left" | "right";

interface LabelRowProps {
    label?: string;
    value?: string;
    units?: string;
    size?: LabelRowSize;
    unitsPosition?: UnitsPosition; // New prop for units position
}

const getLabelFontSize = (size: LabelRowSize) => {
    switch (size) {
        case "mini":
            return "12px";
        case "default":
        default:
            return "13px";
    }
};

const getLabelFontColor = (size: LabelRowSize) => {
    switch (size) {
        case "mini":
            return Colors.grey6;
        case "default":
        default:
            return Colors.grey7;
    }
};

const getValueFontColor = (size: LabelRowSize) => {
    switch (size) {
        case "mini":
            return Colors.grey4;
        case "default":
        default:
            return Colors.grey5;
    }
};

const getUnitsFontColor = (size: LabelRowSize) => {
    switch (size) {
        case "mini":
            return Colors.grey6;
        case "default":
        default:
            return Colors.grey7;
    }
};

const getContainerPadding = (size: LabelRowSize) => {
    switch (size) {
        case "mini":
            return "0 4px 0 2px";
        case "default":
        default:
            return "0 6px 0 4px";
    }
};

const LabelRow: FC<LabelRowProps> = ({
    label,
    value,
    units,
    size = "default",
    unitsPosition = "right",
}) => {
    const unitsOnLeft = units && unitsPosition === "left";
    const unitsOnRight = units && unitsPosition === "right";

    return (
        <LabelContainer $size={size}>
            {(label || unitsOnLeft) && (
                <LabelAndUnitsRow>
                    {label && <Label $size={size}>{label}</Label>}
                    {/* Units on LEFT */}
                    {unitsOnLeft && <Units $size={size}>{units}</Units>}
                </LabelAndUnitsRow>
            )}
            {(value || unitsOnRight) && (
                <ValueRow>
                    {/* Only display Value if it exists */}
                    {value && <Value $size={size}>{value}</Value>}
                    {/* Units on RIGHT */}
                    {unitsOnRight && <Units $size={size}>{units}</Units>}
                </ValueRow>
            )}
        </LabelContainer>
    );
};

export default LabelRow;